/**
 * @file useAnalysisHistory - Hook for accessing and managing analysis history
 *
 * This hook provides convenient access to the analysis history store,
 * with pre-computed selectors for common use cases like:
 * - Viewing all history
 * - Getting recent analyses
 * - Filtering by context (family/work/friends)
 * - Deleting individual items
 * - Clearing all history
 */

import { useAnalysisStore, type AnalysisItem, type ContextType } from '../../../store';

/**
 * Return type for the useAnalysisHistory hook
 */
export interface UseAnalysisHistoryReturn {
  /**
   * All analysis items in history, sorted by timestamp (newest first)
   */
  history: AnalysisItem[];

  /**
   * The 10 most recent analysis items
   */
  recent: AnalysisItem[];

  /**
   * The currently active/displayed analysis
   */
  currentAnalysis: AnalysisItem | null;

  /**
   * Get analyses filtered by context
   */
  getByContext: (context: ContextType) => AnalysisItem[];

  /**
   * Get a specific analysis by ID
   */
  getById: (id: string) => AnalysisItem | undefined;

  /**
   * Delete a specific analysis item
   */
  deleteAnalysis: (id: string) => void;

  /**
   * Clear all history
   */
  clearHistory: () => void;

  /**
   * Set the current analysis being viewed/displayed
   */
  setCurrentAnalysis: (analysis: AnalysisItem | null) => void;
}

/**
 * Hook for accessing and managing analysis history.
 *
 * Provides read access to persisted analysis history and actions
 * for managing individual items or the entire history.
 *
 * @example
 * ```tsx
 * function HistoryScreen() {
 *   const {
 *     recent,
 *     getByContext,
 *     deleteAnalysis,
 *     clearHistory
 *   } = useAnalysisHistory();
 *
 *   const familyAnalyses = getByContext('family');
 *
 *   return (
 *     <View>
 *       <Button onPress={clearHistory}>Clear All</Button>
 *       {recent.map((item) => (
 *         <AnalysisCard
 *           key={item.id}
 *           analysis={item}
 *           onDelete={() => deleteAnalysis(item.id)}
 *         />
 *       ))}
 *     </View>
 *   );
 * }
 * ```
 *
 * @returns {UseAnalysisHistoryReturn} History data and management functions
 */
export function useAnalysisHistory(): UseAnalysisHistoryReturn {
  const history = useAnalysisStore((state) => state.history);
  const currentAnalysis = useAnalysisStore((state) => state.currentAnalysis);
  const getRecent = useAnalysisStore((state) => state.getRecent);
  const getByContext = useAnalysisStore((state) => state.getByContext);
  const getById = useAnalysisStore((state) => state.getById);
  const deleteAnalysis = useAnalysisStore((state) => state.deleteAnalysis);
  const clearHistory = useAnalysisStore((state) => state.clearHistory);
  const setCurrentAnalysis = useAnalysisStore((state) => state.setCurrentAnalysis);

  // Pre-compute recent items (10 most recent)
  const recent = getRecent(10);

  return {
    history,
    recent,
    currentAnalysis,
    getByContext,
    getById,
    deleteAnalysis,
    clearHistory,
    setCurrentAnalysis,
  };
}
